import "./index.scss";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { setEndlessCategories } from "../../Store/store";
import { store } from "../../Store";
import { useDispatch } from "react-redux";

import LoadingScreen from "../LoadingScreen/index";
import GameSelect from "./GameSelect";
import CategorySelect from "./CategorySelect";
import DifficultySelect from "./DifficultySelect";

// Rather than supplying a lose condition and therefore ending this 'Endless Mode' concept,
// do not end the game, and don't allow currentLevelProgress to dip below 0.

let listOfGames = [];
let listOfCategories = [];

/**
 * Endless mode menu component that manages the setup flow for endless gameplay.
 * Guides users through game selection, category selection, and difficulty selection
 * before starting an endless mode session with unlimited rounds.
 *
 * @param {Object} props - Component props
 * @param {Array} props.scoreData - Array of player score data
 * @param {Array} props.levelData - Array of level progression data
 * @param {Object} props.config - Application configuration object
 * @param {Array} props.config.PossibleGames - Array of available game types
 * @returns {JSX.Element} Endless mode setup menu with multi-step selection process
 */

const EndlessModeMenu = ({ scoreData, levelData, config }) => {
  const dispatch = useDispatch();
  const [game, setGame] = useState(false);
  const [categories, setCategories] = useState([]);
  const [display, setDisplay] = useState("loading");
  useEffect(() => {
    findGames();
    setGame(false);
    setCategories([]);
    return () => {
      listOfGames = [];
      listOfCategories = [];
    };
  }, []);

  /**
   * Finds and filters available games for endless mode.
   * Excludes certain game types that don't fit the endless mode format.
   */
  const findGames = () => {
    listOfGames = [];
    config.PossibleGames.forEach((gameType) => {
      if (
        !listOfGames.includes(gameType) &&
        gameType !== "Story Book" &&
        gameType !== "Fill In The Blank" &&
        // These games can be added back in later but don't fit the current mold
        gameType !== "Solve It" &&
        gameType !== "Crocodile" &&
        gameType !== "FilterData" &&
        gameType !== "Verb Activity"
      ) {
        listOfGames.push(gameType);
      }
    });
    setDisplay("games");
  };
  /**
   * Finds available categories for the selected game type.
   * Filters out math-related categories and empty categories.
   *
   * @param {string} game - The selected game type
   */
  const findCategories = (game) => {
    setGame(game);
    setDisplay("categories");
    listOfCategories = [];
    store.getState().store.value.gameData.forEach((datum) => {
      if (datum[game] !== null && !isNaN(Number(datum[game]))) {
        if (
          !listOfCategories.includes(datum.category) &&
          datum.category !== "Math" &&
          datum.category !== "Numbers and Age" &&
          datum.category !== ""
        ) {
          listOfCategories.push(datum.category);
        }
      }
    });
  };
  /**
   * Updates the selected categories list by adding or removing a category.
   * Toggles category selection and updates Redux state.
   *
   * @param {string} newCat - The category to add or remove
   */
  const updateCategories = (newCat) => {
    let newCategories = categories;
    if (newCategories.includes(newCat)) {
      newCategories = newCategories.filter((datum) => datum !== newCat);
    } else {
      newCategories.push(newCat);
    }
    setCategories([...newCategories]);
    dispatch(setEndlessCategories(newCategories));
  };
  /**
   * Resets all selections and returns to the game selection screen.
   */
  const resetSelections = () => {
    setGame(false);
    setCategories([]);
    setDisplay("games");
  };
  let tabHeight = document.getElementsByClassName("topBar-tab")[0];
  if (tabHeight !== undefined) {
    tabHeight = parseInt(tabHeight.style.height, 10) + 5;
  } else {
    tabHeight = 15;
  }
  if (tabHeight > 45) {
    tabHeight = 45;
  }
  if (display === "loading") {
    return <LoadingScreen />;
  } else if (display === "games") {
    return (
      <GameSelect
        findCategories={findCategories}
        games={listOfGames}
        levelData={levelData}
      />
    );
  } else if (display === "categories") {
    return (
      <CategorySelect
        categories={listOfCategories}
        chosenCategories={categories}
        game={game}
        levelData={levelData}
        resetCategories={() => setCategories([])}
        setDisplay={(display) => setDisplay(display)}
        updateCategories={updateCategories}
      />
    );
  } else if (display === "difficulty") {
    return (
      <DifficultySelect
        categories={categories}
        game={game}
        resetSelections={resetSelections}
      />
    );
  }
};

export default EndlessModeMenu;

EndlessModeMenu.propTypes = {
  levelData: PropTypes.array,
};
